import React from "react";
import Modal from "../../UIElements/Modal";
import { Button } from "../../UIElements/Button";
import { useGlobalUtils } from "../../../../lib/globalUtils";
import { useTranslation } from "react-i18next";
import {
  STATUS_LABELS,
  STATUS_BADGE_COLORS,
  getStatusGroup,
  getCustomerName,
  getCustomerPhone,
} from "@finitless/shared/orderConstants";
import "../../../pages/orders/orders-pages.css";

const OrderDetailsModal = ({ order, isOpen, onClose }) => {
  const { t } = useTranslation("orders");
  const { parseDate, parsePrice, calculateEstimatedDelivery } =
    useGlobalUtils();

  if (!order) return null;

  const getIncludedTaxes = (isDeliveryFee = false) => {
    if (!order) return 0;
    if (order?.taxes?.length === 0) {
      return order.tax_type === 1 ? (order?.summary?.tax ?? 0) : 0;
    } else {
      return order?.taxes.reduce((taxIncluded, tax) => {
        return (
          taxIncluded +
          ((!isDeliveryFee && tax.type === 1 && tax.target === "product") ||
          (isDeliveryFee && tax.type === 1 && tax.target === "delivery_fee")
            ? tax.summary?.tax
            : 0)
        );
      }, 0);
    }
  };

  const getIncludedTaxesDiscounts = () => {
    if (!order) return 0;
    return (
      order?.taxes
        ?.filter((tax) => tax?.type === 1 && tax?.target === "product")
        ?.reduce(
          (carry, tax) =>
            carry + (tax?.summary?.tax_after_discount ?? tax?.summary?.tax),
          0,
        ) ?? 0
    );
  };

  const getStatusBadge = (status) => {
    const statusGroup = getStatusGroup(status);
    const statusLabel = t(`statusLabels.${status}`, {
      defaultValue: STATUS_LABELS[status] || status,
    });

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
          STATUS_BADGE_COLORS[statusGroup] || STATUS_BADGE_COLORS.unknown
        }`}
      >
        {statusLabel}
      </span>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("detail.modal.orderNumber", { id: order.id })}
      subtitle={t("detail.header.orderDetails")}
      maxWidth="max-w-2xl"
      className="ordering-order-detail"
    >
      <div className="space-y-6">
        {/* Order Status */}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">
            {t("detail.labels.status")}:
          </span>
          {getStatusBadge(order.status)}
        </div>

        {/* Customer Information */}
        <div>
          <h3 className="text-foreground mb-3 text-lg font-semibold">
            {t("detail.sections.customerInformation")}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.labels.name")}:
              </span>
              <span className="text-foreground">{getCustomerName(order)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.labels.phone")}:
              </span>
              <span className="text-foreground">{getCustomerPhone(order)}</span>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div>
          <h3 className="text-foreground mb-3 text-lg font-semibold">
            {t("detail.sections.orderItems")}
          </h3>
          <div className="space-y-3">
            {(order.products || []).map((product, index) => (
              <div key={product.id || index} className="bg-card rounded-lg p-3">
                {/* Product Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-foreground font-medium">
                      {t("detail.labels.productWithQuantity", {
                        quantity: product.quantity,
                        name: product.name,
                      })}
                    </h4>
                    {product.comment && (
                      <p className="text-muted-foreground mt-1 text-sm">
                        {t("detail.labels.note")}: {product.comment}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 text-right">
                    <div className="text-foreground font-medium">
                      {parsePrice(product.total) || "0.00"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="border-border mt-6 space-y-2 border-t pt-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.summary.subtotal")}:
              </span>
              <span className="text-foreground">
                {parsePrice(
                  (order?.summary?.subtotal ?? order?.subtotal ?? 0) +
                    getIncludedTaxes(),
                )}
              </span>
            </div>
            {(order?.summary?.discount > 0 || order?.discount > 0) &&
              !order?.offers?.length && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {order?.offer_type === 1 && order?.offer_rate ? (
                        <>
                          {t("detail.summary.discount")} ({order.offer_rate}%)
                        </>
                      ) : (
                        <>{t("detail.summary.discount")}</>
                      )}
                      :
                    </span>
                    <span className="text-success">
                      -
                      {parsePrice(
                        order?.summary?.discount ?? order?.discount ?? 0,
                      )}
                    </span>
                  </div>
                  {order?.summary?.subtotal_with_discount > 0 &&
                    order?.summary?.discount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {t("detail.summary.subtotalAfterDiscount")}:
                        </span>
                        <span className="text-foreground">
                          $
                          {parsePrice(
                            order?.tax_type === 1
                              ? order.summary.subtotal_with_discount +
                                  getIncludedTaxesDiscounts()
                              : order.summary.subtotal_with_discount,
                          )}
                        </span>
                      </div>
                    )}
                </>
              )}
            {order?.offers?.length > 0 &&
              order?.offers
                ?.filter((offer) => offer?.target === 1)
                ?.map((offer) => (
                  <React.Fragment key={offer.id}>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {offer.name}
                        {offer.rate_type === 1 && offer?.rate && (
                          <span className="text-muted-foreground">
                            {" "}
                            ({offer.rate}%)
                          </span>
                        )}
                        :
                      </span>
                      <span className="text-success">
                        -{parsePrice(offer?.summary?.discount ?? 0)}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
            {order?.taxes?.length === 0 &&
              order?.tax_type === 2 &&
              order?.summary?.tax > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("detail.summary.tax")}
                    {order?.tax && (
                      <span className="text-muted-foreground">
                        {" "}
                        ({order.tax}%)
                      </span>
                    )}
                    :
                  </span>
                  <span className="text-foreground">
                    {parsePrice(order.summary.tax)}
                  </span>
                </div>
              )}
            {order?.fees?.length === 0 && order?.summary?.service_fee > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("detail.summary.serviceFee")}
                  {order?.service_fee && (
                    <span className="text-muted-foreground">
                      {" "}
                      ({order.service_fee}%)
                    </span>
                  )}
                  :
                </span>
                <span className="text-foreground">
                  {parsePrice(order.summary.service_fee)}
                </span>
              </div>
            )}
            {order?.taxes?.length > 0 &&
              order?.taxes
                ?.filter(
                  (tax) =>
                    tax?.type === 2 &&
                    tax?.rate !== 0 &&
                    tax?.target === "product",
                )
                .map((tax) => (
                  <div key={tax?.id} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {tax?.name || t("detail.summary.tax")}
                      {tax?.rate && (
                        <span className="text-muted-foreground">
                          {" "}
                          ({tax.rate}%)
                        </span>
                      )}
                      :
                    </span>
                    <span className="text-foreground">
                      {parsePrice(
                        tax?.summary?.tax_after_discount ??
                          tax?.summary?.tax ??
                          0,
                      )}
                    </span>
                  </div>
                ))}
            {order?.fees?.length > 0 &&
              order?.fees
                ?.filter((fee) => !(fee?.fixed === 0 && fee?.percentage === 0))
                ?.map((fee) => (
                  <div key={fee.id} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {fee?.name || t("detail.summary.fee")}
                      {(fee?.fixed > 0 || fee?.percentage > 0) && (
                        <span className="text-muted-foreground">
                          {" "}
                          (
                          {fee?.fixed > 0 &&
                            `$${fee.fixed.toFixed(2)}${
                              fee.percentage > 0 ? " + " : ""
                            }`}
                          {fee.percentage > 0 && `${fee.percentage}%`})
                        </span>
                      )}
                      :
                    </span>
                    <span className="text-foreground">
                      {parsePrice(
                        (fee?.summary?.fixed ?? 0) +
                          (fee?.summary?.percentage_after_discount ??
                            fee?.summary?.percentage ??
                            0),
                      )}
                    </span>
                  </div>
                ))}
            {order?.offers?.length > 0 &&
              order?.offers
                ?.filter((offer) => offer?.target === 3)
                ?.map((offer) => (
                  <div key={offer.id} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {offer.name}
                      {offer.rate_type === 1 && offer?.rate && (
                        <span className="text-muted-foreground">
                          {" "}
                          ({offer.rate}%)
                        </span>
                      )}
                      :
                    </span>
                    <span className="text-success">
                      -{parsePrice(offer?.summary?.discount ?? 0)}
                    </span>
                  </div>
                ))}
            {order?.summary?.delivery_price !== undefined &&
              order?.summary?.delivery_price !== null &&
              order?.delivery_type !== 2 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("detail.summary.deliveryFee")}:
                  </span>
                  <span className="text-foreground">
                    {parsePrice(
                      order.summary.delivery_price + getIncludedTaxes(true),
                    )}
                  </span>
                </div>
              )}
            {order?.extra_value_checkprice &&
              order?.extra_value_checkprice > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("detail.summary.extraValueCheckprice")}:
                  </span>
                  <span className="text-foreground">
                    {parsePrice(order.extra_value_checkprice)}
                  </span>
                </div>
              )}
            {order?.taxes?.length > 0 &&
              order?.taxes
                ?.filter(
                  (tax) =>
                    tax?.type === 2 &&
                    tax?.rate !== 0 &&
                    tax?.target === "delivery_fee",
                )
                .map((tax) => (
                  <div key={tax?.id} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {tax?.name || t("detail.summary.tax")}
                      {tax?.rate && (
                        <span className="text-muted-foreground">
                          {" "}
                          ({tax.rate}%)
                        </span>
                      )}
                      :
                    </span>
                    <span className="text-foreground">
                      {parsePrice(
                        tax?.summary?.tax_after_discount ??
                          tax?.summary?.tax ??
                          0,
                      )}
                    </span>
                  </div>
                ))}
            {order?.offers?.length > 0 &&
              order?.offers
                ?.filter((offer) => offer?.target === 2)
                ?.map((offer) => (
                  <div key={offer.id} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {offer.name}
                      {offer.rate_type === 1 && offer?.rate && (
                        <span className="text-muted-foreground">
                          {" "}
                          ({offer.rate}%)
                        </span>
                      )}
                      :
                    </span>
                    <span className="text-success">
                      -{parsePrice(offer?.summary?.discount ?? 0)}
                    </span>
                  </div>
                ))}
            {(order?.summary?.driver_tip > 0 || order?.driver_tip > 0) && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("detail.summary.driverTip")}
                  {order?.driver_tip_rate && (
                    <span className="text-muted-foreground">
                      {" "}
                      ({order.driver_tip_rate}%)
                    </span>
                  )}
                  :
                </span>
                <span className="text-foreground">
                  {parsePrice(
                    order?.summary?.driver_tip ?? order?.totalDriverTip ?? 0,
                  )}
                </span>
              </div>
            )}
            {order?.metafields
              ?.filter(
                (meta) =>
                  meta?.key?.includes("extra_value") &&
                  ![0, -0].includes(parseFloat(meta?.value)),
              )
              ?.map((extra, index) => (
                <div key={index} className="flex justify-between">
                  <span className="text-muted-foreground">{extra?.key}:</span>
                  <span className="text-foreground">
                    {parseFloat(extra?.value) > 0
                      ? parsePrice(parseFloat(extra.value))
                      : `-${parsePrice(parseFloat(extra.value) * -1)}`}
                  </span>
                </div>
              ))}
            <div className="border-border flex justify-between border-t pt-2 text-xl font-bold">
              <span className="text-foreground">
                {t("detail.summary.total")}:
              </span>
              <span className="text-primary">
                {parsePrice(order?.summary?.total ?? 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Delivery Information */}
        <div>
          <h3 className="text-foreground mb-3 text-lg font-semibold">
            {t("detail.sections.deliveryInformation")}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.labels.address")}:
              </span>
              <span className="text-foreground text-right">
                {order?.customer?.address || t("common.notAvailable")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.labels.orderTime")}:
              </span>
              <span className="text-foreground">
                {parseDate(order.created_at, { useUTC: true })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("detail.labels.estimatedDelivery")}:
              </span>
              <span className="text-foreground">
                {parseDate(calculateEstimatedDelivery(order))}
              </span>
            </div>
          </div>
        </div>

        {/* Special Instructions */}
        {order.instructions && (
          <div>
            <h3 className="text-foreground mb-3 text-lg font-semibold">
              {t("detail.sections.specialInstructions")}
            </h3>
            <div className="bg-card rounded-lg p-4">
              <p className="text-foreground">{order.instructions}</p>
            </div>
          </div>
        )}
      </div>

      <div className="border-border mt-6 flex justify-end border-t pt-6">
        <Button
          emphasis="solid"
          intent="brand"
          size="touch"
          onClick={onClose}
          className="px-4"
        >
          {t("detail.actions.close")}
        </Button>
      </div>
    </Modal>
  );
};

export default OrderDetailsModal;
